import React, { useState, useEffect } from 'react';
import { Star, SlidersHorizontal, MoreVertical, Search, Bell, Plus, RefreshCw, Settings } from 'lucide-react';
import Sidebar from './components/Sidebar';
import KanbanColumn from './components/KanbanColumn';
import TableView from './components/TableView';
import TaskModal from './components/TaskModal';
import SettingsManager from './components/SettingsManager';
import { users } from './mockData';
import config from './config.json';
import './App.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function App() {
  const [tasks, setTasks] = useState([]);
  const [selectedTask, setSelectedTask] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newTaskColumn, setNewTaskColumn] = useState(null);
  const [activeView, setActiveView] = useState('tasks');
  const [theme, setTheme] = useState('dark');
  const [draggedTask, setDraggedTask] = useState(null);
  const [draggedOverTask, setDraggedOverTask] = useState(null);
  const [viewMode, setViewMode] = useState('kanban'); // 'kanban' or 'table'
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showSettingsManager, setShowSettingsManager] = useState(false);

  // Load columns from config and filter hidden ones
  const columns = config.columns
    .filter(col => !col.hidden)
    .sort((a, b) => a.order - b.order);

  // Charger les données depuis l'API au démarrage et toutes les 5 secondes
  useEffect(() => {
    fetchTasks();
    
    // Auto-refresh toutes les 5 secondes
    const intervalId = setInterval(() => {
      fetchTasks();
    }, 5000);
    
    // Cleanup : arrêter l'intervalle quand le composant est démonté
    return () => clearInterval(intervalId);
  }, []);

  // Fonction pour récupérer les tâches depuis l'API
  const fetchTasks = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_URL}/today`);
      const result = await response.json();
      
      if (result.success) {
        setTasks(result.cards);
        console.log(`✅ ${result.total} tâches chargées depuis l'API`);
      } else {
        throw new Error(result.error || 'Erreur lors du chargement des données');
      }
    } catch (err) {
      console.error('❌ Erreur lors du chargement des tâches:', err);
      setError(err.message);
      // En cas d'erreur, garder les tâches existantes ou un tableau vide
    } finally {
      setLoading(false);
    }
  };

  // Apply theme to document
  useEffect(() => {
    document.documentElement.className = theme;
  }, [theme]);

  const getTasksByStatus = (status) => {
    return tasks.filter((task) => task.status === status);
  };

  const handleDragStart = (task) => {
    setDraggedTask(task);
  };

  const handleDragOver = (e, task) => {
    e.preventDefault();
    if (task && draggedTask && task.id !== draggedTask.id) {
      setDraggedOverTask(task);
    }
  };

  const handleDrop = (status, droppedOnTask) => {
    if (!draggedTask) return;

    const newTasks = [...tasks];
    const draggedIndex = newTasks.findIndex(t => t.id === draggedTask.id);
    const draggedTaskCopy = { ...newTasks[draggedIndex], status };

    // Remove dragged task from array
    newTasks.splice(draggedIndex, 1);

    if (droppedOnTask) {
      // Drop on another task - insert before it
      const dropIndex = newTasks.findIndex(t => t.id === droppedOnTask.id);
      newTasks.splice(dropIndex, 0, draggedTaskCopy);
    } else {
      // Drop on column - add to end
      const columnTasks = newTasks.filter(t => t.status === status);
      const lastTaskIndex = newTasks.lastIndexOf(columnTasks[columnTasks.length - 1]);
      newTasks.splice(lastTaskIndex + 1, 0, draggedTaskCopy);
    }

    setTasks(newTasks);
    setDraggedTask(null);
    setDraggedOverTask(null);
  };

  const handleEditTask = (task) => {
    setSelectedTask(task);
    setIsModalOpen(true);
    setNewTaskColumn(null);
  };

  const handleAddTask = (columnId) => {
    setSelectedTask(null);
    setNewTaskColumn(columnId);
    setIsModalOpen(true);
  };

  const handleSaveTask = (taskData) => {
    if (selectedTask) {
      setTasks((prevTasks) =>
        prevTasks.map((task) => (task.id === selectedTask.id ? { ...task, ...taskData } : task))
      );
    } else {
      const newTask = {
        ...taskData,
        id: Date.now().toString(),
        status: newTaskColumn || 'todo',
      };
      setTasks((prevTasks) => [...prevTasks, newTask]);
    }
    setIsModalOpen(false);
  };

  const handleDeleteTask = (taskId) => {
    setTasks((prevTasks) => prevTasks.filter((task) => task.id !== taskId));
    setIsModalOpen(false);
  };

  return (
    <div className="flex h-screen bg-background overflow-hidden">
      <Sidebar activeView={activeView} setActiveView={setActiveView} theme={theme} setTheme={setTheme} />

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <header className="h-16 border-b border-border flex items-center justify-between px-6">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-card rounded-lg flex items-center justify-center">
                <span className="text-foreground text-sm">📋</span>
              </div>
              <h1 className="text-foreground text-xl font-semibold">Task Automate</h1>
              <button className="text-muted hover:text-foreground">
                <Star className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Right side */}
          <div className="flex items-center gap-3">
            <button 
              onClick={fetchTasks}
              disabled={loading}
              className="p-2 hover:bg-card-hover rounded-lg transition-colors relative"
              title="Rafraîchir les données"
            >
              <RefreshCw className={`w-5 h-5 text-muted ${loading ? 'animate-spin' : ''}`} />
            </button>
            <button 
              onClick={() => setShowSettingsManager(true)}
              className="p-2 hover:bg-card-hover rounded-lg transition-colors"
              title="Configuration"
            >
              <Settings className="w-5 h-5 text-muted" />
            </button>
            <button className="p-2 hover:bg-card-hover rounded-lg transition-colors">
              <Plus className="w-5 h-5 text-[#6366f1]" />
            </button>
            <div className="flex items-center -space-x-2">
              {users.slice(0, 5).map((user, idx) => (
                <div
                  key={user.id}
                  className="w-8 h-8 rounded-full border-2 border-background overflow-hidden"
                  style={{ zIndex: 10 - idx }}
                >
                  <img src={user.avatar} alt={user.name} className="w-full h-full object-cover" />
                </div>
              ))}
            </div>
            <button className="p-2 hover:bg-card-hover rounded-lg transition-colors relative">
              <Bell className="w-5 h-5 text-muted" />
              <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-red-500 rounded-full"></span>
            </button>
            <button className="p-2 hover:bg-card-hover rounded-lg transition-colors">
              <Search className="w-5 h-5 text-muted" />
            </button>
            <button className="p-2 hover:bg-card-hover rounded-lg transition-colors">
              <MoreVertical className="w-5 h-5 text-muted" />
            </button>
          </div>
        </header>

        {/* Tabs */}
        <div className="border-b border-border px-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-6">
              <button
                onClick={() => setViewMode('kanban')}
                className={`px-3 py-3 font-medium text-sm flex items-center gap-2 border-b-2 transition-colors ${
                  viewMode === 'kanban'
                    ? 'text-foreground border-[#6366f1]'
                    : 'text-muted hover:text-foreground border-transparent'
                }`}
              >
                <span>📊</span>
                Kanban
              </button>
              <button
                onClick={() => setViewMode('table')}
                className={`px-3 py-3 font-medium text-sm flex items-center gap-2 border-b-2 transition-colors ${
                  viewMode === 'table'
                    ? 'text-foreground border-[#6366f1]'
                    : 'text-muted hover:text-foreground border-transparent'
                }`}
              >
                <span>📋</span>
                Table
              </button>
              <button className="px-3 py-3 text-muted hover:text-foreground border-b-2 border-transparent font-medium text-sm flex items-center gap-2">
                <span>📝</span>
                List
              </button>
            </div>
            <button className="flex items-center gap-2 px-3 py-2 text-muted hover:text-foreground hover:bg-card-hover rounded-lg transition-colors text-sm">
              <SlidersHorizontal className="w-4 h-4" />
              Filter
            </button>
          </div>
        </div>

        {/* Kanban Board or Table View */}
        {viewMode === 'kanban' ? (
          <div className="flex-1 overflow-x-auto overflow-y-hidden">
            <div className="p-6 h-full">
              <div className="flex gap-4 h-full">
                {columns.map((column) => {
                  const columnTasks = getTasksByStatus(column.id);
                  return (
                    <KanbanColumn
                      key={column.id}
                      column={column}
                      tasks={columnTasks}
                      onEdit={handleEditTask}
                      onAddTask={handleAddTask}
                      onDragStart={handleDragStart}
                      onDragOver={handleDragOver}
                      onDrop={handleDrop}
                    />
                  );
                })}

                <div className="flex-shrink-0 w-[380px]">
                  <button className="w-full border-2 border-dashed border-[#3a3d45] hover:border-[#4a4d55] rounded-lg p-4 flex items-center justify-center gap-2 text-gray-400 hover:text-gray-300 transition-all group">
                    <Plus className="w-5 h-5" />
                    <span>Ajouter une colonne</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <TableView tasks={tasks} onEdit={handleEditTask} onAddTask={handleAddTask} />
        )}
      </div>

      {/* Task Modal */}
      {isModalOpen && (
        <TaskModal
          task={selectedTask}
          onClose={() => setIsModalOpen(false)}
          onSave={handleSaveTask}
          onDelete={handleDeleteTask}
        />
      )}

      {/* Settings Manager */}
      {showSettingsManager && (
        <SettingsManager
          onClose={() => setShowSettingsManager(false)}
          onSave={() => {
            setShowSettingsManager(false);
            fetchTasks(); // Recharger les données après modification
            window.location.reload(); // Recharger la page pour appliquer les changements de colonnes
          }}
        />
      )}
    </div>
  );
}

export default App;
